import { create } from "zustand";
import { devtools } from "zustand/middleware";
import useLoaderStore from "./useLoaderStore";
import { useToastStore } from "./toastStore";

import { fetchWithHeaders } from "../api/fetchWithHeaders";

interface ConfigStore {
	reportsArray: { data: []; pagination: any };
	fetchReports: (
		page: number,
		pageSize: number,
		negotiationId?: number
	) => void;
	uploadFile: (val: any) => void;
	deleteFile: (val: any) => void;
	regerateTargetnTactics: (val: any) => void;
	getStatusOfTargetnTactics: (val: any) => void;
}

export const useConfigStore = create<ConfigStore>()(
	devtools((set) => ({
		reportsArray: [],
		fetchReports: async (
			page: number,
			pageSize: number,
			negotiationId?: number
		) => {
			const { showLoader } = useLoaderStore.getState();
			const { error } = useToastStore.getState();
			const url = import.meta.env.VITE_API_BASE_URL;

			showLoader(true);
			const params =
				`?page_number=${page}&page_size=${pageSize}` +
				(negotiationId ? `&negotiation_id=${negotiationId}` : "");
			try {
				const resp = await fetchWithHeaders(
					url + "api/v1/file/files" + params,
					{
						method: "GET",
					}
				);
				const data = await resp.json();
				if (!data?.success) {
					error("Error", `Failed to fetch reports: ${data?.message}`);
					return null;
				}

				// adding new field as formatedCreatedDate
				data?.data?.forEach((item: any) => {
					item.formatedCreatedDate = new Date(
						item.created_at
					).toLocaleDateString();
				});

				set({ reportsArray: data });
			} catch (err) {
				showLoader(false);
				console.log(err);
			} finally {
				showLoader(false);
			}
		},
		uploadFile: async (payload: any) => {
			const url = import.meta.env.VITE_API_BASE_URL;
			const form = new FormData();
			form.append("file", payload.file);
			const { error } = useToastStore.getState();
			const { showLoader } = useLoaderStore.getState();
			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/file/upload_file?negotiation_id=${payload.negotiation_id}&index_id=${payload.index_id}`,
					{
						method: "POST",
						body: form,
					}
				);
				const data = await resp.json();
				// Refresh the reports list so UI updates after upload
				try {
					if (!data?.success) {
						error("Error", `Failed to upload file`);
						showLoader(false);
						return null;
					}
					const negId =
						typeof payload?.negotiation_id === "number"
							? payload.negotiation_id
							: payload?.negotiation_id
							? parseInt(payload.negotiation_id)
							: undefined;
					useConfigStore.getState().fetchReports(1, 10, negId);
				} catch (e) {
					// no-op: best-effort refresh
				} finally {
					showLoader(false);
				}
				return data;
			} catch (err) {
				console.log(err);
			} finally {
				showLoader(false);
			}
		},
		deleteFile: async (payload: any) => {
			const url = import.meta.env.VITE_API_BASE_URL;
			const { showLoader } = useLoaderStore.getState();

			showLoader(true);
			const { error } = useToastStore.getState();
			try {
				const resp = await fetchWithHeaders(
					url +
						`api/v1/file/${payload.file_id}?hard_delete=false`,
					{
						method: "DELETE",
					}
				);
				const data = await resp.json();
				if (!data?.success) {
					error("Error", `Failed to delete file: ${data?.message}`);
					showLoader(false);
					return null;
				}
				return data;
			} catch (err) {
				showLoader(false);
				console.log(err);
			} finally {
				showLoader(false);
			}
		},

		regerateTargetnTactics: async (payload: any) => {
			const url = import.meta.env.VITE_API_BASE_URL;
			const { error } = useToastStore.getState();
			const { showLoader } = useLoaderStore.getState();

			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/chat/process-targets-strategy`,
					{
						method: "POST",
						body: JSON.stringify(payload),
					}
				);
				return resp.json();
			} catch (err) {
				error("Error", `Failed to regenerate targets`);
			} finally {
				showLoader(false);
			}
		},

		getStatusOfTargetnTactics: async (task_id: any) => {
			const url = import.meta.env.VITE_API_BASE_URL;
			const { error } = useToastStore.getState();

			const params = `/${task_id}`;
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/chat/process-targets-strategy/status` + params,
					{
						method: "GET",
					}
				);
				return resp.json();
			} catch (err) {
				error("Error", `Failed to fetch the queue status`);
			}
		},
	}))
);
